"use client"

import { type FragmentOf, readFragment } from "@opensea/graphql"
import { UnstyledButton } from "@opensea/ui-kit"
import { useMemo } from "react"
import { CurrencyHeaderWatchlistButtonFragment } from "@/components/currencies/CurrencyWatchlistButton/CurrencyHeaderWatchlistButton.fragment"
import { CurrencyWatchlistIcon } from "@/components/currencies/CurrencyWatchlistButton/CurrencyWatchlistIcon"
import { useToggleWatchToken } from "@/components/currencies/CurrencyWatchlistButton/use-toggle-watch-token"
import { withSuspense } from "@/components/suspense/WithSuspense"
import { useIsTokenWatched } from "@/hooks/api/use-is-token-watched"
import { useIsTokenWatchlistEnabled } from "@/hooks/use-flags"

type CurrencyHeaderWatchlistButtonProps = {
  currency: FragmentOf<typeof CurrencyHeaderWatchlistButtonFragment>
  className?: string
}

export const CurrencyHeaderWatchlistButton = withSuspense(
  function CurrencyHeaderWatchlistButton({
    currency: currencyKey,
    className,
  }: CurrencyHeaderWatchlistButtonProps) {
    const isTokenWatchlistEnabled = useIsTokenWatchlistEnabled()

    const currency = readFragment(
      CurrencyHeaderWatchlistButtonFragment,
      currencyKey,
    )

    const identifiers = useMemo(
      () => [
        {
          contractAddress: currency.contractAddress,
          chainIdentifier: currency.chainIdentifier,
        },
      ],
      [currency.contractAddress, currency.chainIdentifier],
    )

    const { isTokenWatchedMap, isTokenWatchedFetching } =
      useIsTokenWatched(identifiers)

    const key = `${currency.chainIdentifier}:${currency.contractAddress}`
    const isWatching = isTokenWatchedMap.get(key) ?? false

    const toggleWatch = useToggleWatchToken({
      currency,
      isWatching,
    })

    const handleWatch = async (ev: React.MouseEvent<HTMLButtonElement>) => {
      ev.preventDefault()
      await toggleWatch.execute()
    }

    if (!isTokenWatchlistEnabled) {
      return null
    }

    const isFetching = isTokenWatchedFetching || toggleWatch.fetching
    return (
      <UnstyledButton
        className={className}
        disabled={isFetching}
        onClick={handleWatch}
      >
        <CurrencyWatchlistIcon
          isFilled={toggleWatch.isWatching}
          loading={isFetching}
          size={20}
        />
      </UnstyledButton>
    )
  },
  {
    ssr: false,
    fallback: ({ className }) => (
      <UnstyledButton className={className} disabled>
        <CurrencyWatchlistIcon isFilled={false} loading size={20} />
      </UnstyledButton>
    ),
  },
)
